import { useEffect, useRef, useState, type ReactNode } from "react";

type TableScrollWrapProps = {
  /** Contenido a envolver (normalmente una `<table>` ancha). */
  children: ReactNode;
  /** Clases extra para el contenedor externo (shell). */
  className?: string;
  /** Clase del contenedor inferior con el scroll horizontal nativo. */
  wrapClassName?: string;
  /**
   * Etiqueta accesible del contenedor desplazable. El wrap inferior es
   * focuseable (tabIndex 0 + role="region") para que un usuario de teclado
   * pueda recorrer las columnas con flechas en Safari/navegadores donde una
   * tabla sin controles no recibe foco.
   */
  ariaLabel?: string;
};

/**
 * Envuelve una tabla ancha con doble barra de scroll horizontal sincronizada:
 * una barra superior (visible solo cuando hay overflow real) espejo de la
 * barra nativa inferior. Extraído del patrón local de AdminPlayersListPage.
 */
export default function TableScrollWrap({
  children,
  className,
  wrapClassName = "responsive-table-wrap",
  ariaLabel = "Tabla con desplazamiento horizontal",
}: TableScrollWrapProps) {
  const topRef = useRef<HTMLDivElement | null>(null);
  const bottomRef = useRef<HTMLDivElement | null>(null);
  const [scrollWidth, setScrollWidth] = useState<number | null>(null);
  const [hasOverflow, setHasOverflow] = useState(false);

  useEffect(() => {
    const bottom = bottomRef.current;
    if (!bottom) return;

    let frame = 0;
    const measure = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        const width = bottom.scrollWidth;
        setScrollWidth(width);
        setHasOverflow(width > bottom.clientWidth + 1);
        const top = topRef.current;
        if (top) top.scrollLeft = bottom.scrollLeft;
      });
    };

    measure();

    const observer = new ResizeObserver(measure);
    observer.observe(bottom);
    for (const child of Array.from(bottom.children)) {
      observer.observe(child);
    }

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
    // `children` como dependencia re-engancha el observer cuando el contenido
    // de la tabla cambia de estructura (p. ej. la tabla se monta de nuevo).
  }, [children]);

  return (
    <div className={className}>
      {hasOverflow && (
        <div
          ref={topRef}
          className="table-scroll-sync"
          aria-hidden="true"
          tabIndex={-1}
          onScroll={(event) => {
            const bottom = bottomRef.current;
            if (bottom) bottom.scrollLeft = event.currentTarget.scrollLeft;
          }}
        >
          <div
            className="table-scroll-sync__inner"
            style={
              scrollWidth
                ? { width: `${scrollWidth}px`, minWidth: `${scrollWidth}px` }
                : undefined
            }
          />
        </div>
      )}
      <div
        ref={bottomRef}
        className={wrapClassName}
        // Landmark y focus stop SOLO cuando hay overflow real: si la tabla
        // cabe completa no hay nada que desplazar con el teclado.
        tabIndex={hasOverflow ? 0 : undefined}
        role={hasOverflow ? "region" : undefined}
        aria-label={hasOverflow ? ariaLabel : undefined}
        onScroll={(event) => {
          const top = topRef.current;
          if (top) top.scrollLeft = event.currentTarget.scrollLeft;
        }}
      >
        {children}
      </div>
    </div>
  );
}
